import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ModeOptionButton } from '@/components/mode/ModeSwitch';
import { SelectControl } from '@/components/ui/SelectControl';
import { MODE_OPTIONS } from '@/lib/modes';
import { useSupportUnread } from '@/hooks/useSupportUnread';
import { clientEnv } from '@/lib/env';
import { learnerApiFetch, learnerApiJson } from '@/lib/learner-api';
import { subscribePath } from '@/lib/paywall';
import { useLearnerPreferences, type BaseLanguage } from '@/providers/LearnerPreferences';
import { track } from '@/lib/analytics';
import { typeScale } from '@/lib/typography';
import { getCachedNickname, setCachedNickname, setDeviceNickname } from '@/features/games/yaYaApi';

type User = {
  id: string;
  email: string | null;
  display_name: string | null;
  subscribed: boolean;
  subscription_override: boolean;
  subscription_store?: string | null;
};

type UserStats = {
  flashcards_swiped: number;
  game_runs_played: number;
};

export const Route = createFileRoute('/app/settings')({ component: SettingsRoute });

export function SettingsRoute() {
  if (!clientEnv.clerkPublishableKey) return <SettingsPage auth={null} />;
  return <ClerkSettings />;
}

function ClerkSettings() {
  const auth = useAuth();
  return <SettingsPage auth={{ getToken: auth.getToken, isSignedIn: Boolean(auth.isSignedIn), signOut: auth.signOut }} />;
}

function SettingsPage({ auth }: { auth: { getToken: () => Promise<string | null>; isSignedIn: boolean; signOut: () => Promise<void> } | null }) {
  const preferences = useLearnerPreferences();
  const queryClient = useQueryClient();
  const [nickname, setNickname] = useState(getCachedNickname() ?? '');
  const [nicknameStatus, setNicknameStatus] = useState<string | null>(null);
  const unread = useSupportUnread(auth?.getToken ?? null);
  const user = useQuery({
    queryKey: ['learner-user'],
    queryFn: () => learnerApiJson<User>(auth?.getToken ?? null, '/v1/user', { auth: true }),
    enabled: Boolean(auth?.isSignedIn),
  });
  const stats = useQuery({
    queryKey: ['learner-stats'],
    queryFn: () => learnerApiJson<UserStats>(auth?.getToken ?? null, '/v1/user/stats', { auth: true }),
    enabled: Boolean(auth?.isSignedIn),
  });

  useEffect(() => {
    if (user.data?.display_name) {
      setNickname(user.data.display_name);
      setCachedNickname(user.data.display_name);
    }
  }, [user.data?.display_name]);

  const saveNickname = async () => {
    const trimmed = nickname.trim().slice(0, 24);
    if (!trimmed) return;
    const result = auth?.isSignedIn
      ? await learnerApiFetch(auth.getToken, '/v1/user', {
          method: 'PATCH',
          auth: true,
          body: JSON.stringify({ display_name: trimmed }),
        }).then(async (response) => {
          if (response.ok) {
            setCachedNickname(trimmed);
            return { ok: true as const };
          }
          return { ok: false as const, error: 'Could not save that name.' };
        })
      : await setDeviceNickname(auth?.getToken ?? null, trimmed);
    if (result.ok) {
      setNicknameStatus('Saved');
      track('Nickname Set', { source: 'settings', mode: auth?.isSignedIn ? 'account' : 'device' });
      await queryClient.invalidateQueries({ queryKey: ['learner-user'] });
    } else {
      setNicknameStatus(result.error);
    }
  };

  const deleteAccount = async () => {
    if (!auth?.isSignedIn || !window.confirm('Delete your Hmonglus account and saved data? This cannot be undone.')) return;
    const response = await learnerApiFetch(auth.getToken, '/v1/user', { method: 'DELETE', auth: true });
    if (!response.ok) throw new Error('Account deletion failed');
    track('Account Deleted');
    await auth.signOut();
    await queryClient.invalidateQueries();
  };
  const manageSubscription = async () => {
    if (!auth?.isSignedIn) return;
    if (user.data?.subscription_store?.toUpperCase() === 'STRIPE') {
      const result = await learnerApiJson<{ portal_url: string }>(
        auth.getToken,
        '/v1/subscription/create-portal-session',
        { method: 'POST', auth: true },
      );
      window.location.assign(result.portal_url);
      return;
    }
    window.alert('Manage this subscription in the App Store or Play Store account where it was purchased.');
  };
  const restoreSubscription = async () => {
    if (!auth?.isSignedIn) return;
    await learnerApiJson(auth.getToken, '/v1/subscription/verify', {
      method: 'POST',
      auth: true,
    });
    await user.refetch();
    track('Subscription Restore Press', { platform: 'web' });
  };

  return (
    <div className="mx-auto max-w-3xl px-5 py-8 sm:px-8 lg:py-12">
      <h1 className={typeScale.screenTitle}>Settings</h1>

      <SettingsCard title="Account">
        {auth?.isSignedIn ? (
          <div>
            <p className="font-bold">{user.data?.display_name || user.data?.email || 'Signed in'}</p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <StatTile
                value={stats.data ? String(stats.data.flashcards_swiped) : '…'}
                label="Flashcards swiped"
              />
              <StatTile
                value={stats.data ? String(stats.data.game_runs_played) : '…'}
                label="Games played"
              />
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              <button onClick={() => void auth.signOut()} className="rounded-xl border border-slate-300 px-4 py-2 font-semibold">Sign out</button>
              <button onClick={() => void deleteAccount()} className="rounded-xl border border-red-200 px-4 py-2 font-semibold text-red-700">Delete account</button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-slate-600">Create an account to sync and keep your progress.</p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <StatTile value="--" label="Flashcards swiped" empty />
              <StatTile value="--" label="Games played" empty />
            </div>
            <Link to="/auth/sign-in" className="mt-4 inline-block font-bold text-[#2d7a13]">Sign in or create an account</Link>
          </div>
        )}
        <div className="mt-6">
          <p className="font-extrabold">Display name</p>
          <p className="mt-1 text-sm text-slate-500">Shown on game leaderboards.</p>
          <div className="mt-3 flex gap-2">
            <input
              value={nickname}
              maxLength={24}
              onChange={(event) => {
                setNickname(event.target.value.slice(0, 24));
                setNicknameStatus(null);
              }}
              placeholder="Your display name"
              className="min-w-0 flex-1 rounded-xl border border-[#b7dfb0] bg-white px-4 py-2 font-semibold outline-none focus:ring-4 focus:ring-[#399918]/15"
            />
            <button
              type="button"
              disabled={!nickname.trim()}
              onClick={() => void saveNickname()}
              className="rounded-xl bg-[#399918] px-4 py-2 font-bold text-white disabled:opacity-50"
            >
              Save
            </button>
          </div>
          {nicknameStatus ? <p className="mt-2 text-sm text-slate-500">{nicknameStatus}</p> : null}
        </div>
      </SettingsCard>

      <SettingsCard title="Hmonglus Premium">
        {user.data?.subscribed ? (
          <><p className="font-bold text-[#399918]">Premium is active</p><p className="mt-1 text-sm text-slate-500">Your access works across web, iPhone, and Android when you use the same account.</p><button onClick={() => void manageSubscription()} className="mt-4 rounded-xl border border-slate-300 px-4 py-2 font-semibold">Manage subscription</button></>
        ) : (
          <><p className="text-slate-600">Unlock the complete content library and premium games.</p><div className="mt-4 flex flex-wrap gap-3"><a href={subscribePath('settings')} onClick={() => track('Subscription Options Press')} className="inline-block rounded-xl bg-[#399918] px-5 py-3 font-bold text-white">View plans</a>{auth?.isSignedIn && <button onClick={() => void restoreSubscription()} className="rounded-xl border border-slate-300 px-4 py-2 font-semibold">Refresh purchases</button>}</div></>
        )}
      </SettingsCard>

      <SettingsCard title="Learning preferences">
        <div className="mb-6">
          <p className="text-sm font-extrabold uppercase tracking-[0.14em] text-[#2d7a13]">Dialect</p>
          <div className="mt-2 rounded-2xl bg-[#D8EBD2] p-2">
            {MODE_OPTIONS.map((option) => (
              <ModeOptionButton
                key={option}
                option={option}
                selected={option === preferences.dialect}
                onSelect={(value) => {
                  preferences.setDialect(value);
                  track('Settings Preference Press', { preference: 'Dialect', value });
                }}
              />
            ))}
          </div>
        </div>
        <SelectControl
          label="Voice"
          value={preferences.voice}
          onChange={(value) => {
            preferences.setVoice(value as 'adult' | 'child');
            track('Settings Preference Press', { preference: 'Voice', value });
          }}
          options={[['child', 'Child'], ['adult', 'Adult']]}
        />
        <SelectControl
          label="Translation language"
          value={preferences.baseLanguage}
          onChange={(value) => {
            preferences.setBaseLanguage(value as BaseLanguage);
            track('Settings Preference Press', { preference: 'Translation language', value });
          }}
          options={[
            ['en', 'English'],
            ['fr', 'Français'],
            ['es', 'Español'],
            ['de', 'Deutsch'],
            ['it', 'Italiano'],
            ['vi', 'Tiếng Việt'],
            ['lo', 'ລາວ'],
            ['th', 'ไทย'],
          ]}
        />
      </SettingsCard>

      <SettingsCard title="Support">
        <div className="flex items-center gap-2">
          <p className="text-slate-600">Report a bug, ask a question, or share an idea. We reply here.</p>
          {unread.data ? <span data-testid="support-unread-dot" className="h-2.5 w-2.5 rounded-full bg-[#E53935]" /> : null}
        </div>
        <Link
          to="/app/support"
          onClick={() => track('Support Entry Press', { has_unread: Boolean(unread.data) })}
          className="mt-4 inline-block rounded-xl bg-[#399918] px-5 py-3 font-bold text-white"
        >
          {unread.data ? 'View reply' : 'Message support'}
        </Link>
      </SettingsCard>

      <div className="mt-8 flex gap-5 text-sm text-slate-500"><Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link></div>
    </div>
  );
}

function StatTile({ value, label, empty }: { value: string; label: string; empty?: boolean }) {
  return (
    <div className={`rounded-2xl px-4 py-4 ${empty ? 'bg-slate-50' : 'bg-[#ECFFE6]'}`}>
      <p className={`text-2xl font-black ${empty ? 'text-slate-300' : 'text-[#2d7a13]'}`}>{value}</p>
      <p className="mt-1 text-sm font-semibold text-slate-500">{label}</p>
    </div>
  );
}

function SettingsCard({ title, children }: React.PropsWithChildren<{ title: string }>) {
  return (
    <section className="mt-7 rounded-3xl border border-[#cfe8c6] bg-white p-6">
      <h2 className={`mb-5 ${typeScale.settingsCardTitle}`}>{title}</h2>
      {children}
    </section>
  );
}
